/**
 * Enhanced Authentication Context
 *
 * Supports two authentication modes:
 * 1. Anonymous Auth - For public routes (current behavior)
 * 2. Full Auth - For protected routes with user accounts
 *
 * Features:
 * - User profile data from Firestore
 * - Role-based access control (user, admin)
 * - Token refresh management
 * - Loading states
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { auth, db } from "../firebase";
import {
  onIdTokenChanged,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { useCompanyNamesLoad } from "../hooks/useCompanyNamesLoad";
import { withTimeout } from "../utils/withTimeout";

// User roles
export const UserRoles = {
  USER: "user",
  ADMIN: "admin",
};

// Auth states
export const AuthStates = {
  LOADING: "loading",
  ANONYMOUS: "anonymous",
  AUTHENTICATED: "authenticated",
  UNAUTHENTICATED: "unauthenticated",
};

const AuthContext = createContext({
  // User state
  user: null,
  userProfile: null,
  idToken: null,
  authState: AuthStates.LOADING,

  // Computed properties
  isAuthenticated: false,
  isAnonymous: false,
  isAdmin: false,
  isLoading: true,

  // Auth methods
  signIn: async () => {},
  signOut: async () => {},
  signInAnonymous: async () => {},
  resetPassword: async () => {},
  changePassword: async () => {},
  refreshToken: async () => {},

  // Profile methods
  updateProfile: async () => {},
  refreshUserProfile: async () => {},
});

export const AuthProvider = ({ children }) => {
  // Core state
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [idToken, setIdToken] = useState(null);
  // Seed role from the last-known cached value so admin status — and the
  // sidebar/dashboard tools gated on it — is correct INSTANTLY on refresh,
  // rather than flashing non-admin while the token claims / profile resolve.
  const [role, setRole] = useState(() => {
    try {
      return localStorage.getItem("cz_auth_role") || null;
    } catch {
      return null;
    }
  });
  const [authState, setAuthState] = useState(AuthStates.LOADING);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetch user profile from Firestore
   */
  /**
   * @param recordActivity write lastLogin/loginCount. Default true.
   *   ⚠️ FALSE when re-reading the profile after the user saved it — that is a
   *   profile edit, not a session, and counting it would inflate the figure.
   */
  const fetchUserProfile = useCallback(async (uid, recordActivity = true) => {
    try {
      // Guard the read with a timeout so a stalled Firestore connection can't
      // freeze the auth gate (and therefore the whole app) on refresh.
      const userDoc = await withTimeout(getDoc(doc(db, "users", uid)), 10000);
      if (userDoc.exists()) {
        const profile = userDoc.data();
        setUserProfile(profile);

        /**
         * ⚠️ THIS IS LAST *ACTIVE*, and it fires far more often than "login".
         *
         * The app listens on **onIdTokenChanged**, not onAuthStateChanged, and
         * force-refreshes the token every 50 minutes. So this runs on sign-in,
         * on every page load, on every token refresh, and whenever a callable
         * needs a fresh token — which the health board, making four calls a
         * load and polling, triggers readily. Lee had "just now" against his
         * name having only browsed dashboards, and 34 "logins".
         *
         * So `loginCount` was never a login count. It is throttled now, and
         * a REAL sign-in count is recorded separately in signIn() below, which
         * is the only place credentials are actually entered.
         *
         * Throttled to 15 minutes: last-active is useful, but it does not need
         * a Firestore write every time a token rotates.
         */
        if (recordActivity) {
          const lastMs = profile.lastLogin?.toDate
            ? profile.lastLogin.toDate().getTime()
            : 0;
          if (Date.now() - lastMs > 15 * 60 * 1000) {
            updateDoc(doc(db, "users", uid), {
              lastLogin: serverTimestamp(),
              activeSpells: increment(1),
            }).catch((error) => {
              console.warn(
                "Failed to update lastActive (non-blocking):",
                error,
              );
            });
          }
        }

        return profile;
      }
      return null;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  }, []);

  // The shared company names are read once SOMEONE IS SIGNED IN, not as the app starts: a read
  // made before sign-in is refused (see hooks/useCompanyNamesLoad.js for what that cost).
  useCompanyNamesLoad(user ? user.uid : null);

  // Persist the resolved role so the next refresh can seed it instantly (above).
  useEffect(() => {
    try {
      if (role) localStorage.setItem("cz_auth_role", role);
      else localStorage.removeItem("cz_auth_role");
    } catch {
      /* ignore storage errors (e.g. Safari private mode) */
    }
  }, [role]);

  /**
   * Handle auth state changes
   */
  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    const handleUser = async (firebaseUser) => {
      // The `finally` guarantees the auth gate (ProtectedRoute's
      // "Checking authentication…" spinner) ALWAYS resolves, even if the token
      // read stalls — otherwise the whole app hangs on a spinner forever.
      try {
        if (!firebaseUser) {
          // Keep role + userProfile (and the role cache) through this null. On an
          // unstable connection onIdTokenChanged can fire a transient null and
          // then immediately re-fire with the user; if we cleared role/profile
          // here, admin status would get stuck false (the re-fire doesn't restore
          // role when the token lacks the claim) → the sidebar/dashboard drop to
          // non-admin tools mid-session. Only an explicit signOut() truly clears
          // them. The route guard still redirects to /login while UNAUTHENTICATED,
          // so a retained role can't expose the dashboard to a logged-out user.
          setUser(null);
          setIdToken(null);
          setAuthState(AuthStates.UNAUTHENTICATED);
          return;
        }

        // Role is set server-side and travels in the token's custom claims, so
        // admin status is available immediately. Guard the read with a timeout.
        let tokenResult = null;
        try {
          tokenResult = await withTimeout(
            firebaseUser.getIdTokenResult(),
            8000,
          );
        } catch (e) {
          console.warn(
            "getIdTokenResult stalled — proceeding; role falls back to profile:",
            e.message,
          );
        }
        const claimsRole = tokenResult?.claims?.role || null;
        setIdToken(tokenResult?.token || null);
        // Only overwrite role when the claims actually carry one — otherwise keep
        // the cached/last-known role (so a token that lacks the claim doesn't
        // momentarily drop admin → non-admin).
        if (claimsRole) setRole(claimsRole);
        setUser(firebaseUser);

        if (firebaseUser.isAnonymous) {
          setRole(null);
          setAuthState(AuthStates.ANONYMOUS);
          setUserProfile(null);
        } else {
          setAuthState(AuthStates.AUTHENTICATED);
          const profilePromise = fetchUserProfile(firebaseUser.uid).then(
            (profile) => {
              if (!profile) {
                console.warn(
                  "Authenticated user without profile:",
                  firebaseUser.uid,
                );
              } else if (!claimsRole && profile.role) {
                // No role claim in the token — fall back to the profile's role.
                setRole(profile.role);
              }
              return profile;
            },
          );

          // On a FRESH login (no role claim AND no cached role) the dashboard would
          // otherwise render before the profile arrives — flashing non-admin tools
          // and a partial name, then correcting a moment later. Briefly wait for the
          // profile (max 4s) so it renders complete. On refresh the cached role
          // makes admin status instant, so we skip the wait and stay fast.
          let hasCachedRole = false;
          try {
            hasCachedRole = !!localStorage.getItem("cz_auth_role");
          } catch {
            /* ignore */
          }
          if (!claimsRole && !hasCachedRole) {
            await Promise.race([
              profilePromise,
              new Promise((resolve) => setTimeout(resolve, 4000)),
            ]);
          }
        }
      } catch (err) {
        console.error("Auth state handler error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    // Wait for Firebase to FINISH restoring the persisted session before we react
    // to auth changes. Otherwise onIdTokenChanged can fire a transient null on
    // load (before the restore completes), which bounces the user to /login and
    // then straight back when the real session resolves.
    auth
      .authStateReady()
      .then(() => {
        if (cancelled) return;
        unsubscribe = onIdTokenChanged(auth, handleUser);
      })
      .catch((err) => {
        console.warn("authStateReady failed:", err?.message);
        setIsLoading(false);
      });

    // Hard safety net: release the gate after 12s if auth never settles.
    const safetyNet = setTimeout(() => {
      setIsLoading((stillLoading) => {
        if (stillLoading) {
          console.warn("Auth init exceeded 12s — releasing the route guard.");
        }
        return false;
      });
    }, 12000);

    return () => {
      cancelled = true;
      unsubscribe();
      clearTimeout(safetyNet);
    };
  }, [fetchUserProfile]);

  /**
   * Periodic token refresh (every 50 minutes)
   */
  useEffect(() => {
    const interval = setInterval(
      async () => {
        if (auth.currentUser) {
          try {
            const freshToken = await auth.currentUser.getIdToken(true);
            setIdToken(freshToken);
          } catch (err) {
            console.error("Error refreshing token:", err);
          }
        }
      },
      50 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, []);

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(async (email, password) => {
    try {
      setIsLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      // ⚠️ THE ONLY place a password is actually entered, so the only honest
      // place to count a login. Fire-and-forget: never block sign-in on a
      // statistic.
      updateDoc(doc(db, "users", result.user.uid), {
        lastSignInAt: serverTimestamp(),
        signInCount: increment(1),
      }).catch(() => {});
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Sign in error:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      return {
        success: false,
        error: getAuthErrorMessage(error.code),
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setUserProfile(null);
      setIdToken(null);
      setRole(null);
      setAuthState(AuthStates.UNAUTHENTICATED);
      return { success: true };
    } catch (error) {
      console.error("Sign out error:", error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Sign in anonymously (for public routes)
   */
  const signInAnonymous = useCallback(async () => {
    try {
      const result = await signInAnonymously(auth);
      return { success: true, user: result.user };
    } catch (error) {
      console.error("Anonymous sign in error:", error);
      return { success: false, error: error.message };
    }
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error("Password reset error:", error);
      return {
        success: false,
        error: getAuthErrorMessage(error.code),
      };
    }
  }, []);

  /**
   * Change password (requires re-authentication)
   */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      if (!auth.currentUser || !auth.currentUser.email) {
        return { success: false, error: "No authenticated user" };
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword,
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update password
      await updatePassword(auth.currentUser, newPassword);
      return { success: true };
    } catch (error) {
      console.error("Change password error:", error);
      return {
        success: false,
        error: getAuthErrorMessage(error.code),
      };
    }
  }, []);

  /**
   * Refresh token manually
   */
  const refreshToken = useCallback(async () => {
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken(true);
      setIdToken(token);
      return token;
    }
    return null;
  }, []);

  /**
   * Update user profile in Firestore
   */
  const updateProfile = useCallback(
    async (updates) => {
      if (!user || user.isAnonymous) {
        return { success: false, error: "No authenticated user" };
      }

      try {
        await updateDoc(doc(db, "users", user.uid), {
          ...updates,
          updatedAt: serverTimestamp(),
        });

        // Refresh profile. recordActivity: false — saving a profile is not a
        // session, and counting it would inflate the activity figure.
        await fetchUserProfile(user.uid, false);
        return { success: true };
      } catch (error) {
        console.error("Update profile error:", error);
        return { success: false, error: error.message };
      }
    },
    [user, fetchUserProfile],
  );

  /**
   * Refresh user profile from Firestore
   * Useful after updating profile data directly
   */
  const refreshUserProfile = useCallback(async () => {
    if (!user?.uid) {
      return { success: false, error: "No user logged in" };
    }

    try {
      await fetchUserProfile(user.uid);
      return { success: true };
    } catch (error) {
      console.error("Refresh profile error:", error);
      return { success: false, error: error.message };
    }
  }, [user, fetchUserProfile]);

  // Computed properties
  const isAuthenticated = authState === AuthStates.AUTHENTICATED;
  const isAnonymous = authState === AuthStates.ANONYMOUS;
  // Prefer the role from the token claims (instant + reliable); fall back to the
  // Firestore profile for any user whose claims predate role-sync. This stops
  // admin status (and therefore the sidebar/dashboard tools) from being lost when
  // the profile read is slow or fails on refresh.
  const effectiveRole = role || userProfile?.role || null;
  const isAdmin = effectiveRole === UserRoles.ADMIN;

  const value = {
    // State
    user,
    userProfile,
    idToken,
    authState,
    isLoading,

    // Computed
    isAuthenticated,
    isAnonymous,
    isAdmin,

    // Methods
    signIn,
    signOut,
    signInAnonymous,
    resetPassword,
    changePassword,
    refreshToken,
    updateProfile,
    refreshUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Hook for components that need anonymous auth (public routes)
 * Automatically signs in anonymously if not authenticated
 */
export const useAnonymousAuth = () => {
  const auth = useAuth();

  useEffect(() => {
    if (auth.authState === AuthStates.UNAUTHENTICATED) {
      auth.signInAnonymous();
    }
  }, [auth]);

  return auth;
};

/**
 * Convert Firebase auth error codes to user-friendly messages
 */
function getAuthErrorMessage(code) {
  const messages = {
    "auth/invalid-email": "Invalid email address",
    "auth/user-disabled": "This account has been disabled",
    "auth/user-not-found": "No account found with this email",
    "auth/wrong-password": "Incorrect password",
    "auth/email-already-in-use": "An account with this email already exists",
    "auth/weak-password": "Password should be at least 6 characters",
    "auth/too-many-requests": "Too many attempts. Please try again later",
    "auth/invalid-credential": "Invalid email or password",
    "auth/requires-recent-login":
      "Please sign in again to complete this action",
    "auth/operation-not-allowed":
      "Email/password sign-in is not enabled. Please contact an administrator.",
  };
  return messages[code] || "An error occurred. Please try again";
}

export default AuthContext;
